/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
import { Node } from 'konva/types/Node';
import { compose, flatten, map, nth, partition } from 'rambda';

/**
 * Restrict position to its container
 *
 * @param {object} coords {x, y} coords
 * @param {object} containerDimensions container with {width, height}
 * @param {object} dimensions (optional) dimensions to account for when restricting position
 * @returns {object} new {x,y} coords that do not exceed container
 */
export const makeGetRestrictedPosition = ({ containerDimensions, dimensions = { width: 0, height: 0 }, scale = 1 }: any) => {
  const upperBound = 0;
  const leftBound = 0;
  const rightBound = containerDimensions.width - dimensions.width;
  const bottomBound = containerDimensions.height - dimensions.height;

  return (coords: { x: number; y: number }) => {
    // restrict point between bounds
    let xPos = Math.min(Math.max(coords.x, leftBound), rightBound * scale);
    let yPos = Math.min(Math.max(coords.y, upperBound), bottomBound * scale);
    xPos = xPos < 0.03 ? 0 : xPos;
    yPos = yPos < 0.03 ? 0 : yPos;
    return {
      x: xPos,
      y: yPos,
    };
  };
};

/**
 * Get the relative position of a pointer
 *
 * @param {*} e pointer pointer event
 * @param {*} invertedTransform transform to apply
 * @return {{x: number, y: number}}
 */
export const getRelativePointerPosition = (e: { target: Node }, invertedTransform: { point: (arg0: any) => any }) => {
  // get pointer (say mouse or touch) position
  const pos = e.target?.getStage()?.getPointerPosition() || { x: 0, y: 0 };
  // now we can find relative point
  return invertedTransform.point(pos);
};

const makeCircle = (props: { x: any; y: any }, scale: number) => ({
  ...props,
  radius: 5 / scale,
  fill: 'white',
  strokeWidth: 1 / scale,
});

/**
 * Create circles with coordinates based on points array
 */
export function makeCirclesFromPoints(points: [number, number][], scale: any) {
  return points.map(([x, y]) => makeCircle({ x, y }, scale));
}

/**
 * Gets all the points between the existing points in a points array
 */
function getMidPoints(points: any[]) {
  const midPoints = points.map((pt: any[], i: number) => {
    const adjacentPt = i === points.length - 1 ? points[0] : points[i + 1];

    return [
      // get the middle
      (adjacentPt[0] + pt[0]) / 2,
      (adjacentPt[1] + pt[1]) / 2,
    ];
  });

  return midPoints;
}

/**
 * It's the hypotenuse
 */
function getDistanceBetweenPoints([x1, y1]: [number, number], [x2, y2]: [number, number]) {
  const a = Math.abs(x1 - x2);
  const b = Math.abs(y1 - y2);
  return Math.sqrt(a * a + b * b);
}

const MAX_ALLOWED_DISTANCE = 70;

export function getNearestMidPoint(points: any, { x, y }: { x: number; y: number }) {
  const midPoints = getMidPoints(points);

  let shortestDistance = 0;
  let nearestPoint = null;
  let nearestPointIndex = 0;

  midPoints.forEach(([x2, y2]: any, i: number) => {
    const distance = getDistanceBetweenPoints([x, y], [x2, y2]);

    // distance must be less than
    if (distance < MAX_ALLOWED_DISTANCE) {
      if (shortestDistance === 0) {
        shortestDistance = distance;
        nearestPoint = [x2, y2];
        nearestPointIndex = i;
      } else if (distance > 0 && distance < shortestDistance) {
        shortestDistance = distance;
        nearestPoint = [x2, y2];
        nearestPointIndex = i;
      }
    }
  });

  return nearestPoint ? { point: nearestPoint, pointIndex: nearestPointIndex + 1 } : null;
}

const isEven = (x: number) => x % 2 === 0;

export function splitEvenAndOdd(list: unknown[]): number[][] {
  // @ts-ignore
  return compose(map(map(nth(1))), partition(compose(isEven, Number, nth(0))), Object.entries)(list || []);
}

export function getPolygonBounds(points: any[]) {
  const [xCoordList, yCoordList] = splitEvenAndOdd(flatten(points));
  const x = Math.min(...xCoordList);
  const y = Math.min(...yCoordList);
  return {
    x,
    y,
    width: Math.max(...xCoordList) - x,
    height: Math.max(...yCoordList) - y,
  };
}

export function makePolygonDimensions(props: { points: [number, number][] }) {
  return { ...getPolygonBounds(props.points) };
}

export function getLineRect(lineElement: { getClientRect: () => any }, container: { getClientRect: () => any }) {
  const imageRect = container.getClientRect();
  const clientRect = lineElement.getClientRect();
  return {
    ...clientRect,
    y: clientRect.y - imageRect.y,
    x: clientRect.x - imageRect.x,
  };
}

/**
 * Convenience for getting both width and height
 * @param {*} node Konva node
 */
export const getDimensions = (node: Node) => ({
  width: node.width(),
  height: node.height(),
});
